import { Button } from "@mui/material";
import { checkUserRight } from "../../utils/utils";
import moment from "moment";

// A PR item's `allocations[].storeId` and a location autocomplete's `.id` may each be
// either a plain Location id or a populated `{ id | _id, ... }` object depending on the
// call site -- normalize both down to a plain id before comparing, mirroring the
// backend's own refId() (subway-finance-v2-api/src/utils/refId.js).
export const normalizeLocationId = (value) =>
  value && typeof value === "object" ? value.id ?? value._id : value;

// Finds the persisted per-store allocation on a PR item matching the given location id --
// the single source of truth for that item's balance at that store. Returns null (never a
// guessed/rebuilt value) when the item has no allocation for that store, e.g. before a
// Delivery Location has been chosen on a multi-store PR.
export const getAllocationForItem = (prItem, locationId) => {
  if (locationId === null || locationId === undefined) return null;
  const resolvedLocationId = normalizeLocationId(locationId);
  return (
    (prItem?.allocations || []).find(
      (allocation) =>
        String(normalizeLocationId(allocation.storeId)) === String(resolvedLocationId)
    ) || null
  );
};

// Mirrors the backend's computeAvailable (allocation.service.js) -- never trust the
// serialized `availableQuantity` virtual alone since some response shapes may omit it.
export const computeAllocationAvailable = (allocation) =>
  (allocation?.requestedQuantity || 0) -
  (allocation?.reservedQuantity || 0) -
  (allocation?.convertedQuantity || 0) -
  (allocation?.shortClosedQuantity || 0);

export const getHeaderConfig = (editVendor) => {
  const headers = [
    {
      label: "Name",
      id: "name",
    },
    {
      label: "Site",
      id: "site",
    },
    {
      label: "PO Type",
      id: "poType",
    },
    {
      label: "Status",
      id: "status",
    },
    {
      label: "Actions",
      id: "actions",
      render: (data) => {
        return (
          <Button variant="text" onClick={() => editVendor(data, "EDIT")}>
            View
          </Button>
        );
      },
    },
  ];
  return headers;
};

export const PO_TYPES = [
  { label: "Capex", value: "capex_po" },
  { label: "Opex", value: "opex_po" },
];

export const getPOAmount = (lineItems) => {
  let sum = 0;
  Object.keys(lineItems).forEach((key) => {
    lineItems[key].forEach((item) => {
      sum = +sum + +getTotalAmount(item);
    });
  });

  return sum?.toFixed?.(2);
};

const getTotalAmount = (item, taxKey = "taxRate") => {
  const { rate = 0, quantity = 0 } = item;
  const taxRate = item?.[taxKey] || 0;
  let taxAmount = rate * quantity * (taxRate / 100);
  return rate * quantity + +taxAmount;
};

export const getPOLineItemAmount = (lineItems, taxKey = "taxRate") => {
  let sum = 0;
  lineItems.forEach((item) => {
    sum = +sum + +getTotalAmount(item, taxKey);
  });

  return sum?.toFixed?.(2);
};

export const getPOAmountWithoutTax = (lineItems) => {
  let sum = 0;
  Object.keys(lineItems).forEach((key) => {
    lineItems[key].forEach((item) => {
      const { rate = 0, quantity = 0 } = item;
      sum = +sum + rate * quantity;
    });
  });

  return sum?.toFixed?.(2);
};
export const getPOTaxAmount = (lineItems) => {
  let sum = 0;
  Object.keys(lineItems).forEach((key) => {
    lineItems[key].forEach((item) => {
      const { rate = 0, quantity = 0, taxRate = 0 } = item;
      let taxAmount = rate * quantity * (taxRate / 100);
      sum = +sum + taxAmount;
    });
  });

  return sum?.toFixed?.(2);
};

export const getExtraChargesTotal = (extraCharges = [], poSubtotalBeforeTax = 0) => {
  let sum = 0;
  (extraCharges || []).forEach((charge) => {
    if (!charge?.affectsPOTotal) return;

    let { chargeAmount, taxAmount } = charge;

    if (
      chargeAmount === undefined ||
      chargeAmount === null ||
      taxAmount === undefined ||
      taxAmount === null
    ) {
      const value = Number(charge?.value) || 0;
      chargeAmount =
        charge?.calculationType === "PERCENTAGE"
          ? ((Number(poSubtotalBeforeTax) || 0) * value) / 100
          : value;
      taxAmount = charge?.taxable
        ? (chargeAmount * (Number(charge?.taxRate) || 0)) / 100
        : 0;
    }

    sum += (Number(chargeAmount) || 0) + (Number(taxAmount) || 0);
  });
  return sum;
};

export const getAdvanceAmount = (poAmount, percentage) => {
  if (percentage > 0) {
    return (percentage * poAmount) / 100;
  } else {
    return 0;
  }
};

export const availableBudgetAmount = (lineItems) => {
  let sum = 0;
  lineItems
    .filter((i) => ["approval_in_progress", "approved"].includes(i.status))
    .forEach((item) => {
      sum = sum + +item.availableAmount;
    });

  return sum?.toFixed?.(2);
};

export const createPOavailableBudgetAmount = (lineItems, includeItems = []) => {
  let sum = 0;
  lineItems
    ?.filter?.(
      (i) =>
        ["approval_in_progress", "approved"].includes(i.status) &&
        includeItems.includes(i.id)
    )
    ?.forEach((item) => {
      sum = sum + +item.availableAmount;
    });

  return sum?.toFixed?.(2);
};

export const getPurchaseOrderAmount = (lineItems) => {
  let sum = 0;
  lineItems?.forEach?.((item) => {
    sum += item.quantity * (1 + item.taxRate / 100) * item.rate;
  });

  return sum?.toFixed?.(2);
};

export const getPurchaseOrderWithoutTaxAmount = (lineItems) => {
  let sum = 0;
  lineItems?.forEach?.((item) => {
    sum += item.quantity * item.rate;
  });
  return sum?.toFixed?.(2);
};

export const getPurchaseOrderTaxAmount = (lineItems) => {
  let sum = 0;
  lineItems?.forEach?.((item) => {
    sum += (item.rate * item.quantity * item.taxRate) / 100;
  });

  return sum?.toFixed?.(2);
};

// Whether any extra charge line is saved on the PO (or on a PO copy held on an Approval).
export const hasPOExtraCharges = (poDetails = {}) =>
  (poDetails?.extraCharges || []).length > 0;

// Extra charges part of the PO total (affectsPOTotal charges, incl. their tax). Prefers the
// backend-computed extraChargesTotal, same fallback as getPOGrandTotal below.
export const getPOExtraChargesTotal = (poDetails = {}) => {
  if (poDetails?.extraChargesTotal !== undefined && poDetails?.extraChargesTotal !== null) {
    return Number(poDetails.extraChargesTotal).toFixed(2);
  }
  const subtotalBeforeTax = Number(getPurchaseOrderWithoutTaxAmount(poDetails?.items || [])) || 0;
  return getExtraChargesTotal(poDetails?.extraCharges || [], subtotalBeforeTax).toFixed(2);
};

export const formatPOExtraCharges = (poDetails = {}, emptyValue = "-") =>
  hasPOExtraCharges(poDetails) ? getPOExtraChargesTotal(poDetails) : emptyValue;

export const formatPOLocationLabel = (location, emptyValue = "-") => {
  if (!location) return emptyValue;
  if (typeof location !== "object") {
    return typeof location === "string" && location.trim()
      ? location.trim()
      : emptyValue;
  }
  const { code = "", name = "" } = location;
  return (code && name ? `${code} - ${name}` : code || name) || emptyValue;
};

export const formatPOLocationAddress = (location, emptyValue = "-") => {
  if (!location) return emptyValue;
  if (typeof location !== "object") {
    return formatPOLocationLabel(location, emptyValue);
  }
  const { address = "", city = "", state = "" } = location;
  return (
    [formatPOLocationLabel(location, ""), address, city, state]
      .map((part) => String(part || "").replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .join(", ") || emptyValue
  );
};

// Same figure for a PO copy held on an Approval (approval.document). Always recomputed from its
// extraCharges rows: resend refreshes document.extraCharges but not any stored extraChargesTotal.
export const getApprovalPOExtraChargesTotal = (document = {}) => {
  const subtotalBeforeTax = Number(getPurchaseOrderWithoutTaxAmount(document?.items || [])) || 0;
  return getExtraChargesTotal(document?.extraCharges || [], subtotalBeforeTax).toFixed(2);
};

// Prefers the backend-computed grandTotal (items total + affectsPOTotal extra charges);
// falls back to a client-side recompute for POs fetched before that field existed.
export const getPOGrandTotal = (poDetails = {}) => {
  if (poDetails?.grandTotal !== undefined && poDetails?.grandTotal !== null) {
    return Number(poDetails.grandTotal).toFixed(2);
  }
  const items = poDetails?.items || [];
  const itemsTotal = Number(getPurchaseOrderAmount(items));
  const subtotalBeforeTax = Number(getPurchaseOrderWithoutTaxAmount(items)) || 0;
  return (
    itemsTotal +
    getExtraChargesTotal(poDetails?.extraCharges || [], subtotalBeforeTax)
  ).toFixed(2);
};

export const getHeaderPOItemsDetailsConfig = (editRecord, poDetails) => {
  const headers = [
    {
      label: "Main Category",
      id: "mainCategory",
      render: (data) => {
        const { category = {} } = data || {};
        return (category && category.name) || "";
      },
    },
    {
      label: "Sub Category",
      id: "subcategory",
      render: (data) => {
        const { subCategory = {} } = data || {};
        return (subCategory && subCategory.name) || "";
      },
    },
    {
      label: "Item Name",
      id: "itemName",
      render: (data) => {
        const { item = {} } = data;
        return (item && item.name) || "";
      },
    },
    {
      label: "HSN Code",
      id: "hsnCode",
    },
    {
      label: "UOM",
      id: "uom",
    },
    {
      label: "Qty",
      id: "quantity",
    },
    {
      label: "Inward Qty",
      id: "inwardedQuantity",
    },
    {
      label: "Price",
      id: "rate",
    },
    {
      label: "Amt",
      id: "amount",
    },
    {
      label: "Tax rate",
      id: "taxRate",
    },
    {
      label: "Tax Amt",
      id: "taxAmount",
    },
    {
      label: "Total Amt",
      id: "totalAmount",
    },
    {
      label: "Description",
      id: "description",
    },
    {
      label: "Remarks",
      id: "remarks",
    },
    {
      label: "Ticket No.",
      id: "ticketNumber",
    },
    {
      label: "Ticket Creation Date",
      id: "ticketCreationDate",
      render: (data) => {
        return (
          (data?.ticketCreationDate &&
            moment(data.ticketCreationDate).format("DD-MM-YYYY")) ||
          ""
        );
      },
    },
    {
      label: "Short close",
      id: "name",
      render: (data) => {
        if (data.isShortClosed) {
          return "Item Short Closed";
        }
        return (
          <>
            {
              <Button
                variant="text"
                onClick={() => editRecord(data._id)}
                disabled={
                  data.isShortClosed ||
                  !data.allowInward ||
                  poDetails?.status !== "approved" ||
                  !!poDetails?.viewOnly ||
                  !checkUserRight("po.update")
                }
              >
                Short Close
              </Button>
            }
          </>
        );
      },
    },
  ];

  return headers;
};

export const PO_ITEM_MODEL = {
  id: Math.random(),
  mainCategoryName: "",
  mainCategoryId: "",
  subcategoryName: "",
  subcategoryId: "",
  name: "",
  uom: "",
  quantity: 0,
  rate: 0,
  taxRate: 0,
  specification: "",
  itemId: "",
  lineItemId: "",
  refRate: 0,
  lineItemName: "",
  availableAmount: 0,
  ticketNumber: "",
  ticketCreationDate: "",
};

export const UOM_LIST = [
  { label: "Unit", value: "UNIT" },
  { label: "MTR", value: "MTR" },
  {
    label: "Kg",
    value: "KG",
  },
  {
    label: "Ltr",
    value: "LTR",
  },
  {
    label: "Yard",
    value: "YARD",
  },
  {
    label: "Pack And Pack Pack Size",
    value: "PACK_AND_PACK_SIZE",
  },
  {
    label: "Set",
    value: "SET",
  },
  {
    label: "Square Feet",
    value: "SQUARE_FEET",
  },
  {
    label: "Nos",
    value: "NOS",
  },
];
